import {
  differenceInCalendarDays,
  eachDayOfInterval,
  endOfWeek,
  format,
  isSameDay,
  isWeekend,
  startOfDay,
  subDays,
} from "date-fns";
import { OnboardingPhase } from "@forgepoint/shared";
import { parseDateOnly } from "./onboardingDates";
import { GanttPhaseRow } from "./GanttPhaseRow";
import { BAR_STATUS_CLASS, GanttBar } from "./ganttStatus";

interface OnboardingGanttProps {
  phases: OnboardingPhase[];
  scheduleStart: string;
  scheduleEnd: string;
  goLiveDate: string | null;
}

/**
 * The go-live schedule timeline: one row per phase, one day per column, with
 * today and go-live markers. The timeline is bounded by the plan's schedule
 * range; a phase's bar runs until the next phase begins (the last phase runs
 * to the schedule end), clamped into the range. Phases starting after the
 * schedule end are omitted.
 */
export function OnboardingGantt({
  phases,
  scheduleStart,
  scheduleEnd,
  goLiveDate,
}: OnboardingGanttProps) {
  const today = startOfDay(new Date());

  const timelineStart = parseDateOnly(scheduleStart);
  const rangeEnd = parseDateOnly(scheduleEnd);
  const goLive = goLiveDate ? parseDateOnly(goLiveDate) : null;
  const lastAnchor = goLive && goLive > rangeEnd ? goLive : rangeEnd;
  const timelineEnd = endOfWeek(lastAnchor, { weekStartsOn: 1 });

  if (timelineStart > timelineEnd) {
    return null;
  }

  const days = eachDayOfInterval({ start: timelineStart, end: timelineEnd });

  const visiblePhases = phases.filter(
    (phase) => parseDateOnly(phase.startDate) <= rangeEnd,
  );
  const bars: GanttBar[] = visiblePhases.map((phase, index) => {
    const phaseStart = parseDateOnly(phase.startDate);
    const nextStart =
      index < visiblePhases.length - 1
        ? parseDateOnly(visiblePhases[index + 1].startDate)
        : null;
    const defaultEnd = nextStart ? subDays(nextStart, 1) : rangeEnd;
    const barStart = phaseStart < timelineStart ? timelineStart : phaseStart;
    const barEnd = defaultEnd > rangeEnd ? rangeEnd : defaultEnd;
    const status: GanttBar["status"] =
      barEnd < today ? "done" : barStart > today ? "upcoming" : "active";
    return { phase, barStart, barEnd, status };
  });

  const columnFor = (date: Date): number =>
    differenceInCalendarDays(date, timelineStart) + 2;
  const gridTemplateColumns = `minmax(150px, 200px) ${days
    .map((day) => (isWeekend(day) ? "minmax(8px, 0.4fr)" : "minmax(22px, 1fr)"))
    .join(" ")}`;
  const monthSpans = buildMonthSpans(days);
  const phaseRowStart = 3;
  const lastRow = phaseRowStart + bars.length;

  return (
    <div className="overflow-x-auto">
      <div className="grid min-w-[560px]" style={{ gridTemplateColumns }}>
        {/* month header */}
        <div className="sticky left-0 z-10 bg-card" />
        {monthSpans.map((span) => (
          <div
            key={span.label}
            className="pb-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground"
            style={{
              gridColumn: `${span.startColumn} / span ${span.length}`,
              gridRow: 1,
            }}
          >
            {span.label}
          </div>
        ))}

        {/* day header */}
        <div
          className="sticky left-0 z-10 bg-card"
          style={{ gridRow: 2, gridColumn: 1 }}
        />
        {days.map((day, index) => (
          <div
            key={day.toISOString()}
            className={`border-b pb-1 text-center text-[9px] ${
              goLive && isSameDay(day, goLive)
                ? "font-bold text-success"
                : isSameDay(day, today)
                  ? "font-bold text-foreground"
                  : "text-muted-foreground"
            }`}
            style={{ gridColumn: index + 2, gridRow: 2 }}
          >
            {isWeekend(day) ? "" : format(day, "d")}
          </div>
        ))}

        {/* weekend tint */}
        {days.map(
          (day, index) =>
            isWeekend(day) && (
              <div
                key={`weekend-${day.toISOString()}`}
                className="bg-muted/50"
                style={{
                  gridColumn: index + 2,
                  gridRow: `2 / ${lastRow}`,
                }}
              />
            ),
        )}

        {/* phase rows */}
        {bars.map((bar, index) => (
          <GanttPhaseRow
            key={bar.phase.id}
            bar={bar}
            row={phaseRowStart + index}
            columnFor={columnFor}
          />
        ))}

        {/* today marker */}
        {today >= timelineStart && today <= days[days.length - 1] && (
          <div
            className="pointer-events-none border-l-2 border-dashed border-foreground/50"
            style={{
              gridColumn: columnFor(today),
              gridRow: `2 / ${lastRow}`,
            }}
          />
        )}

        {/* go-live marker */}
        {goLive && goLive >= timelineStart && goLive <= timelineEnd && (
          <div
            className="pointer-events-none border-l-2 border-success"
            style={{
              gridColumn: columnFor(goLive),
              gridRow: `2 / ${lastRow}`,
            }}
          />
        )}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-4 text-[10px] text-muted-foreground">
        <span className="flex items-center gap-1.5">
          <span className={`h-2 w-4 rounded-sm ${BAR_STATUS_CLASS.done}`} />{" "}
          Done
        </span>
        <span className="flex items-center gap-1.5">
          <span className={`h-2 w-4 rounded-sm ${BAR_STATUS_CLASS.active}`} />{" "}
          In progress
        </span>
        <span className="flex items-center gap-1.5">
          <span className={`h-2 w-4 rounded-sm ${BAR_STATUS_CLASS.upcoming}`} />{" "}
          Upcoming
        </span>
        {goLive && (
          <span className="flex items-center gap-1.5 font-medium text-success">
            <span className="h-3 border-l-2 border-success" /> Go-live{" "}
            {format(goLive, "EEE, MMM d")}
          </span>
        )}
      </div>
    </div>
  );
}

interface MonthSpan {
  label: string;
  startColumn: number;
  length: number;
}

/**
 * Groups consecutive timeline days into month header spans.
 */
function buildMonthSpans(days: Date[]): MonthSpan[] {
  const spans: MonthSpan[] = [];
  days.forEach((day, index) => {
    const label = format(day, "MMM yyyy");
    const current = spans[spans.length - 1];
    if (current && current.label === label) {
      current.length += 1;
      return;
    }
    spans.push({ label, startColumn: index + 2, length: 1 });
  });
  return spans;
}
